// Torneos por equipos.

import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from '../query-client';
import {
  Nullable,
  Tournament,
  TournamentFixAnalysis,
  TournamentTeam,
  TournamentTeamMatch,
  UserWithToken,
  WithData,
} from '../api.types';
import { USER_BASIC_FIELDS, USER_WITH_COSSY } from '../fragments';

// Tournament Team Management Functions
export const getTournamentTeams = (tournamentId: number, user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<TournamentTeam[]>>(
    gql`
      ${USER_WITH_COSSY}
      query tournamentTeamsByTournament($tournamentId: Int!) {
        data: tournamentTeamsByTournament(tournamentId: $tournamentId) {
          id
          name
          position
          wins
          losses
          draws
          points
          dropRound
          players {
            id
            position
            deckName
            user {
              ...UserWithCossy
            }
          }
        }
      }
    `,
    { tournamentId }
  );

export const createTournamentTeam = (
  user: Nullable<UserWithToken>,
  input: {
    tournamentId: number;
    name: string;
    playerIds: number[];
  }
) =>
  getGraphQLQueryClient(user).request(
    gql`
      ${USER_WITH_COSSY}
      mutation createTournamentTeam($input: CreateTournamentTeamInput!) {
        data: createTournamentTeam(input: $input) {
          id
          name

          players {
            id
            position
            user {
              ...UserWithCossy
            }
          }
        }
      }
    `,
    { input }
  );

export const updateTournamentTeam = (
  user: Nullable<UserWithToken>,
  teamId: number,
  input: {
    name?: string;
    playerIds?: number[];
    position?: number;
    wins?: number;
    losses?: number;
    draws?: number;
    points?: number;
  }
) =>
  getGraphQLQueryClient(user).request(
    gql`
      ${USER_WITH_COSSY}
      mutation updateTournamentTeam($id: Int!, $input: UpdateTournamentTeamInput!) {
        data: updateTournamentTeam(id: $id, input: $input) {
          id
          name
          position
          wins
          losses
          draws
          points
          players {
            id
            position
            user {
              ...UserWithCossy
            }
          }
        }
      }
    `,
    { id: teamId, input }
  );

export const deleteTournamentTeam = (user: Nullable<UserWithToken>, teamId: number) =>
  getGraphQLQueryClient(user).request(
    gql`
      mutation deleteTournamentTeam($id: Int!) {
        data: deleteTournamentTeam(id: $id) {
          id
        }
      }
    `,
    { id: teamId }
  );

export const dropTournamentTeam = (user: Nullable<UserWithToken>, teamId: number) =>
  getGraphQLQueryClient(user).request(
    gql`
      ${USER_WITH_COSSY}
      mutation dropTournamentTeam($id: Int!) {
        data: dropTournamentTeam(id: $id) {
          id
          name
          dropRound
          players {
            id
            position
            user {
              ...UserWithCossy
            }
          }
        }
      }
    `,
    { id: teamId }
  );

export const updateTournamentTeamPlayer = (
  user: Nullable<UserWithToken>,
  teamPlayerId: number,
  input: {
    deckName?: string;
  }
) =>
  getGraphQLQueryClient(user).request<{
    data: { id: number; deckName: string };
  }>(
    gql`
      ${USER_BASIC_FIELDS}
      mutation updateTournamentTeamPlayer($id: Int!, $input: UpdateTournamentTeamPlayerInput!) {
        data: updateTournamentTeamPlayer(id: $id, input: $input) {
          id
          deckName
          position
          user {
            ...UserBasicFields
          }
        }
      }
    `,
    { id: teamPlayerId, input }
  );

export const auditTournamentResults = (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<{
    data: {
      players: {
        playerId: number;
        playerName: string;
        playerCossyId: string | null;
        recordedWins: number;
        recordedLosses: number;
        recordedDraws: number;
        recordedPoints: number;
        actualWins: number;
        actualLosses: number;
        actualDraws: number;
        actualPoints: number;
        hasDiscrepancy: boolean;
        winsDiff: number;
        lossesDiff: number;
        drawsDiff: number;
        pointsDiff: number;
      }[];
      allowsDraws: boolean;
    };
  }>(
    gql`
      query auditTournamentResults($tournamentId: Int!) {
        data: auditTournamentResults(tournamentId: $tournamentId) {
          players {
            playerId
            playerName
            playerCossyId
            recordedWins
            recordedLosses
            recordedDraws
            recordedPoints
            actualWins
            actualLosses
            actualDraws
            actualPoints
            hasDiscrepancy
            winsDiff
            lossesDiff
            drawsDiff
            pointsDiff
          }
          allowsDraws
        }
      }
    `,
    { tournamentId }
  );

/**
 * Qué cambio(s) en los matches explicarían la diferencia entre los standings
 * cargados a mano y el recálculo. Es una query: no escribe nada.
 */
export const getTournamentFixScenarios = (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentFixAnalysis>>(
    gql`
      query tournamentFixScenarios($tournamentId: Int!) {
        data: tournamentFixScenarios(tournamentId: $tournamentId) {
          baselineDistance
          exact
          unique
          searchTruncated
          scenarios {
            distance
            changes {
              matchId
              round
              tableNumber
              player1Name
              player2Name
              currentDescription
              proposedDescription
            }
            effects {
              userId
              playerName
              baselineWins
              baselineLosses
              baselineDraws
              baselinePosition
              nextWins
              nextLosses
              nextDraws
              nextPosition
            }
          }
        }
      }
    `,
    { tournamentId }
  );

// Team Tournament Round Management

/**
 * Empareja una ronda de un torneo por equipos (suizo de equipos).
 * `repairRound` sólo sabe emparejar jugadores sueltos: un torneo por equipos no
 * tiene TournamentPlayer, así que con él la ronda quedaba vacía.
 */
export const generateTournamentTeamPairings = (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number
) =>
  getGraphQLQueryClient(user).request<WithData<Pick<Tournament, 'id'>>>(
    gql`
      mutation generateTournamentTeamPairings($tournamentId: Int!, $round: Int!) {
        data: generateTournamentTeamPairings(
          tournamentId: $tournamentId
          round: $round
        ) {
          id
        }
      }
    `,
    { tournamentId, round }
  );

export const pairTournamentTeamRound = (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number,
  teamMatches: {
    tableNumber: number;
    team1Id: number | null;
    team2Id: number | null;
  }[]
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      ${USER_BASIC_FIELDS}
      ${USER_WITH_COSSY}
      mutation pairTournamentTeamRound(
        $tournamentId: Int!
        $round: Int!
        $teamMatches: [TournamentTeamMatchInput!]!
      ) {
        data: pairTournamentTeamRound(
          tournamentId: $tournamentId
          round: $round
          teamMatches: $teamMatches
        ) {
          id
          currentRound
          status
          tournamentTeamMatches {
            id
            round
            tableNumber
            status
            team1 {
              id
              name
              players {
                id
                position
                deckName
                user {
                  ...UserWithCossy
                }
              }
            }
            team2 {
              id
              name
              players {
                id
                position
                deckName
                user {
                  ...UserWithCossy
                }
              }
            }
            winnerTeam {
              id
              name
            }
            individualMatches {
              id
              matchNumber
              status
              replayUrl
              player1 {
                ...UserBasicFields
                duelingBookId
              }
              player2 {
                ...UserBasicFields
                duelingBookId
              }
              winner {
                ...UserBasicFields
              }
            }
          }
        }
      }
    `,
    { tournamentId, round, teamMatches }
  );

export const reportTournamentTeamMatch = (
  user: Nullable<UserWithToken>,
  teamMatchId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentTeamMatch>>(
    gql`
      mutation reportTournamentTeamMatch($teamMatchId: Int!) {
        data: reportTournamentTeamMatch(teamMatchId: $teamMatchId) {
          id
          status
          winnerTeamId
          winnerTeam {
            id
            name
          }
          individualMatches {
            id
            matchNumber
            status
            replayUrl
            winner {
              id
            }
          }
        }
      }
    `,
    { teamMatchId }
  );

export const clearTournamentTeamMatch = (
  user: Nullable<UserWithToken>,
  teamMatchId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentTeamMatch>>(
    gql`
      mutation clearTournamentTeamMatch($teamMatchId: Int!) {
        data: clearTournamentTeamMatch(teamMatchId: $teamMatchId) {
          id
          status
          winnerTeamId
          individualMatches {
            id
            matchNumber
            status
            replayUrl
            winner {
              id
            }
          }
        }
      }
    `,
    { teamMatchId }
  );

export const setTournamentTeamMatchWinner = (
  user: Nullable<UserWithToken>,
  teamMatchId: number,
  winnerTeamId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentTeamMatch>>(
    gql`
      mutation setTournamentTeamMatchWinner($teamMatchId: Int!, $winnerTeamId: Int!) {
        data: setTournamentTeamMatchWinner(
          teamMatchId: $teamMatchId
          winnerTeamId: $winnerTeamId
        ) {
          id
          status
          winnerTeamId
          winnerTeam {
            id
            name
          }
        }
      }
    `,
    { teamMatchId, winnerTeamId }
  );

export const finishTournamentTeamRound = (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation finishTournamentTeamRound($tournamentId: Int!, $round: Int!) {
        data: finishTournamentTeamRound(tournamentId: $tournamentId, round: $round) {
          id
          currentRound
          status
        }
      }
    `,
    { tournamentId, round }
  );

export const repairTournamentTeamRound = (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation repairTournamentTeamRound($tournamentId: Int!, $round: Int!) {
        data: repairTournamentTeamRound(tournamentId: $tournamentId, round: $round) {
          id
          currentRound
          status
        }
      }
    `,
    { tournamentId, round }
  );

export const migrateTeamsToTop = (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation migrateTeamsToTop($tournamentId: Int!) {
        data: migrateTeamsToTop(tournamentId: $tournamentId) {
          id
          title
          status
        }
      }
    `,
    { tournamentId }
  );
